import { PayloadAction, createSlice } from '@reduxjs/toolkit'

import { AppDispatch, GetState, RootState } from '@SonarCX/store'

import { deleteTag, getTags, postTag } from '../lib/api'
import { track } from '@SonarCX/lib/mixpanel'
import { AddErrorAlert, AddSuccessAlert } from './alert'

type TagState = {
  tags: string[]
  loaded: boolean
  loading: boolean
  error: any
}

const initialState: TagState = {
  tags: [],
  loaded: false,
  loading: false,
  error: null
}

export const tagSlice = createSlice({
  name: 'tag',
  initialState,
  reducers: {
    loading: (state: TagState) => {
      state.loading = true
    },

    load: (state: TagState, { payload }: PayloadAction<string[]>) => {
      state.tags = payload
      state.loaded = true
      state.loading = false
    },

    add: (state: TagState, { payload }: PayloadAction<string[]>) => {
      state.tags = [...state.tags, ...payload]
    },

    remove: (state: TagState, { payload }: PayloadAction<string>) => {
      state.tags = state.tags.filter(tag => tag !== payload)
    },

    failed: (state: TagState, { payload }: PayloadAction<any>) => {
      state.error = payload
      state.loaded = true
      state.loading = false
    }
  }
})

export const { loading, load, add, failed, remove } = tagSlice.actions

// Asynchronous thunk action
export function fetchTags () {
  return async (dispatch: AppDispatch) => {
    dispatch(loading())

    try {
      const tags = await getTags()
      dispatch(load(tags))
    } catch (error) {
      dispatch(AddErrorAlert(error))
    }
  }
}

// Asynchronous thunk action
export function addTag (tag) {
  return async (dispatch: AppDispatch, getState: GetState) => {
    try {
      const { tags } = selectTag(getState())
      // tag already exists, dont save it
      if (tags.includes(tag)) return

      await postTag(tag)
      dispatch(add([tag]))
    } catch (error) {
      dispatch(AddErrorAlert(error))
    }
  }
}

// Asynchronous thunk action
export function addTags (tags) {
  return async (dispatch: AppDispatch) => {
    try {
      await Promise.all(
        tags.map(async tag => {
          postTag(tag).catch(err => {
            dispatch(failed(err))
            console.error('Post error for tag in agent message', err)
          })
        })
      )
      dispatch(load(tags))
    } catch (error) {
      dispatch(failed(error))
    }
  }
}

export function removeTag (tag) {
  return async (dispatch: AppDispatch) => {
    try {
      track('TAG_DELETED', { tag })
      await deleteTag(tag)
      dispatch(remove(tag))
      dispatch(AddSuccessAlert('Tag removed'))
    } catch (err) {
      dispatch(AddErrorAlert(err))
    }
  }
}

export const selectTag = (state: RootState) => state.tag

export default tagSlice.reducer
